/*
 * Pure HdcReport → view-model mappers for the Experian HDC+ report.
 * No React here: every function tolerates null/undefined anywhere in the input
 * and returns display-ready strings ("—" for missing values).
 */
import type {
  HdcAccountValue,
  HdcCharacterDate,
  HdcReport,
  HdcSectorCounts,
  HdcSectorDates,
} from "../../../../ExperianPrivateResponse.types";
import {
  DASH,
  fmtDateTime,
  fmtDocNumber,
  fmtMoney,
  fmtMonth,
  fmtMonthYear,
  fmtNumber,
  fmtPercent,
  fmtText,
  fmtYesNo,
  isNum,
} from "./format.ts";

export type Tone = "ok" | "warn" | "bad" | "muted" | "neutral";

export type TabId =
  | "resumen"
  | "obligaciones"
  | "tarjetas"
  | "cuentas"
  | "global"
  | "evolucion"
  | "microcredito";

export interface TabInfo {
  id: TabId;
  label: string;
  /** Row count shown as a badge, or null for no badge. */
  badge: number | null;
  /** Amber badge: at least one row has an overdue balance. */
  warn: boolean;
}

export interface LabeledValue {
  label: string;
  value: string;
  tone: Tone;
}

export interface ProductVM {
  code: string;
  desc: string;
  tone: Tone;
  consultDate: string;
  securityCode: string;
  executionDatetime: string;
  document: string;
}

export interface PersonVM {
  fullName: string;
  idDesc: string;
  idNumber: string;
  status: string;
  issue: string;
  age: string;
  gender: string;
}

export interface ScoreVM {
  value: string;
  /** 0–100, null when the score is missing. */
  pct: number | null;
  max: string;
  model: string;
  date: string;
  reasons: string[];
}

export interface KpiVM {
  label: string;
  value: string;
  sub: string;
  tone: Tone;
}

export type VectorKind = "ok" | "m30" | "m60" | "m90" | "none" | "other";

export interface VectorCell {
  ch: string;
  kind: VectorKind;
  label: string;
}

export interface StatusVM {
  text: string;
  tone: Tone;
}

export interface LiabilityRowVM {
  entity: string;
  type: string;
  account: string;
  debtor: string;
  status: StatusVM;
  opened: string;
  initial: string;
  balance: string;
  overdue: string;
  hasOverdue: boolean;
  quota: string;
  installments: string;
  rating: string;
  vector: VectorCell[];
}

export interface CardRowVM {
  entity: string;
  franchise: string;
  cardClass: string;
  account: string;
  status: StatusVM;
  limit: string;
  balance: string;
  available: string;
  /** 0–100 (can exceed 100 when over-limit), null when the limit is unknown/zero. */
  usagePct: number | null;
  quota: string;
  overdue: string;
  hasOverdue: boolean;
  vector: VectorCell[];
}

export interface SavingsRowVM {
  entity: string;
  type: string;
  account: string;
  opened: string;
  status: string;
  city: string;
}

export interface InquiryRowVM {
  date: string;
  entity: string;
  product: string;
  city: string;
  reason: string;
}

export interface GlobalRowVM {
  entity: string;
  sector: string;
  rating: string;
  credit: string;
  count: string;
  capital: string;
  guarantee: string;
  guaranteeValue: string;
}

export interface GlobalIndebtednessVM {
  source: string;
  cutoff: string;
  rows: GlobalRowVM[];
}

export interface TrimesterVM {
  label: string;
  /** Raw balance (thousands of COP) used for the bar height; null if missing. */
  balanceRaw: number | null;
  balance: string;
  quota: string;
  delinquency: string;
  delinquencyTone: Tone;
  rating: string;
  use: string;
  opened: string;
  closed: string;
  score: string;
}

export interface VectorRowVM {
  label: string;
  isSector: boolean;
  /** One cell per entry of `months`; "-" (sin info) where the account has no data for that month. */
  cells: VectorCell[];
}

export interface VectorAnalysisVM {
  /** Column labels ("OCT", …), one per behaviourDate. */
  months: string[];
  rows: VectorRowVM[];
}

export interface ProfileRowVM {
  label: string;
  fin: string;
  coop: string;
  retail: string;
  telco: string;
  principal: string;
  other: string;
}

// region helpers

const arr = <T>(a?: (T | null | undefined)[] | null): T[] =>
  Array.isArray(a) ? (a.filter((x) => x !== null && x !== undefined) as T[]) : [];

const firstText = (...vals: (string | null | undefined)[]): string => {
  for (const v of vals) {
    const t = fmtText(v);
    if (t !== DASH) return t;
  }
  return DASH;
};

const dateValue = (s?: string | null): number | null => {
  if (!s) return null;
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? null : t;
};

/** The monthly value with the newest behaviourDate; falls back to the first entry when no dates parse. */
export const latestValue = (
  values?: (HdcAccountValue | null)[] | null,
): HdcAccountValue | null => {
  const list = arr(values);
  if (list.length === 0) return null;
  let best: HdcAccountValue | null = null;
  let bestTime = -Infinity;
  for (const v of list) {
    const t = dateValue(v.behaviourDate);
    if (t !== null && t > bestTime) {
      best = v;
      bestTime = t;
    }
  }
  return best ?? list[0];
};

const VECTOR_LABELS: Record<VectorKind, string> = {
  ok: "Al día",
  m30: "Mora 30",
  m60: "Mora 60",
  m90: "Mora ≥ 90",
  none: "Sin info",
  other: "Otro",
};

export const vectorCell = (raw?: string | null): VectorCell => {
  const ch = (raw ?? "").trim().toUpperCase();
  if (ch === "N") return { ch: "N", kind: "ok", label: VECTOR_LABELS.ok };
  if (ch === "1") return { ch: "1", kind: "m30", label: VECTOR_LABELS.m30 };
  if (ch === "2") return { ch: "2", kind: "m60", label: VECTOR_LABELS.m60 };
  if (/^[3-9]$/.test(ch)) return { ch, kind: "m90", label: VECTOR_LABELS.m90 };
  if (ch === "" || ch === "-")
    return { ch: "–", kind: "none", label: VECTOR_LABELS.none };
  return { ch: ch.slice(0, 1), kind: "other", label: `Código ${ch}` };
};

/**
 * Behaviour vector string → cells, kept in the order Experian sends it
 * (most recent month first, per the design; not confirmed against the spec).
 */
export const parseVector = (v?: string | null, max = 24): VectorCell[] => {
  if (!v) return [];
  return v.slice(0, max).split("").map(vectorCell);
};

/**
 * Best-effort tone for an account status description. Experian's status code
 * table isn't available here, so this keys off the description text plus the
 * overdue balance.
 */
export const statusTone = (
  desc?: string | null,
  overdue?: number | null,
): Tone => {
  if (isNum(overdue) && overdue > 0) return "warn";
  const d = (desc ?? "").toLowerCase();
  if (!d) return "neutral";
  if (/castig|dudoso|cobro jur|embarg|cartera recuperada/.test(d)) return "bad";
  if (/mora/.test(d)) return "warn";
  if (/pagad|cancelad|saldad|cerrad|inactiv/.test(d)) return "muted";
  if (/al d[ií]a|vigente|activ|normal/.test(d)) return "ok";
  return "neutral";
};

// endregion

// region report shape

/** True when Experian returned no credit history (e.g. responseCode "09"). */
export const isEmptyReport = (r?: HdcReport | null): boolean => {
  if (!r) return true;
  if (r.productResult?.responseCode === "09") return true;
  return (
    arr(r.savings).length === 0 &&
    arr(r.liabilities).length === 0 &&
    arr(r.creditCard).length === 0 &&
    arr(r.globalIndebtedness).length === 0 &&
    arr(r.inquiryFootprints).length === 0 &&
    arr(r.models).length === 0 &&
    !r.agregatedInfo &&
    !r.agregatedInfoMicrocredit
  );
};

const overdueOf = (values?: (HdcAccountValue | null)[] | null) => {
  const v = latestValue(values)?.businessValueBalanceOverdue;
  return isNum(v) && v > 0;
};

/** Tabs to show, in order; tabs whose source data is empty are left out. */
export const getTabs = (r?: HdcReport | null): TabInfo[] => {
  if (!r) return [];
  const liabilities = arr(r.liabilities);
  const cards = arr(r.creditCard);
  const savingsAndInquiries =
    arr(r.savings).length + arr(r.inquiryFootprints).length;
  const global = arr(r.globalIndebtedness);
  const trimesters = arr(r.agregatedInfo?.debtEvolution?.trimester);
  const micro = r.agregatedInfoMicrocredit;
  const hasMicro =
    arr(micro?.vectorAnalysis?.sector).length > 0 ||
    !!micro?.overview?.generalProfile;

  const tabs: (TabInfo & { show: boolean })[] = [
    { id: "resumen", label: "Resumen", badge: null, warn: false, show: true },
    {
      id: "obligaciones",
      label: "Obligaciones",
      badge: liabilities.length,
      warn: liabilities.some((l) => overdueOf(l.values)),
      show: liabilities.length > 0,
    },
    {
      id: "tarjetas",
      label: "Tarjetas de crédito",
      badge: cards.length,
      warn: cards.some((c) => overdueOf(c.values)),
      show: cards.length > 0,
    },
    {
      id: "cuentas",
      label: "Cuentas y consultas",
      badge: savingsAndInquiries,
      warn: false,
      show: savingsAndInquiries > 0,
    },
    {
      id: "global",
      label: "Endeudamiento global",
      badge: global.length,
      warn: false,
      show: global.length > 0,
    },
    {
      id: "evolucion",
      label: "Evolución de la deuda",
      badge: null,
      warn: false,
      show: trimesters.length > 0,
    },
    {
      id: "microcredito",
      label: "Microcrédito",
      badge: null,
      warn: false,
      show: hasMicro,
    },
  ];
  return tabs.filter((t) => t.show).map(({ show, ...t }) => t);
};

// endregion

// region resumen

export const mapProductResult = (
  r?: HdcReport | null,
  executionDatetime?: string | null,
): ProductVM => {
  const p = r?.productResult;
  const code = fmtText(p?.responseCode);
  const personId =
    r?.identifyingAttributes?.personId ?? r?.basicInformation?.personId;
  const idNumber = fmtDocNumber(personId?.personIdNumber);
  return {
    code,
    desc: fmtText(p?.responseDesc),
    tone: p?.responseCode === "13" ? "ok" : "warn",
    consultDate: fmtText(p?.consultDate),
    securityCode: fmtText(p?.securityCode),
    executionDatetime: fmtDateTime(executionDatetime),
    document:
      idNumber === DASH
        ? DASH
        : [fmtText(personId?.personIdDesc), idNumber]
            .filter((x) => x !== DASH)
            .join(" · "),
  };
};

export const mapChecks = (r?: HdcReport | null): LabeledValue[] => {
  const b = r?.basicInformation;
  const intl = arr(b?.demographic?.internationalOperations);
  const intlFlag = intl.some((o) => o.internationalOperationsIndicator === true)
    ? true
    : intl.length > 0
    ? false
    : null;
  const disputes = r?.agregatedInfo?.overview?.principals?.currentDisputes;
  const official = b?.officialSourceValidationStatus;
  const rut = b?.citizenTaxRegistrationIndicator;
  return [
    {
      label: "Validación fuente oficial",
      value: official === true ? "OK" : official === false ? "NO OK" : DASH,
      tone: official === true ? "ok" : official === false ? "warn" : "neutral",
    },
    {
      label: "Registro tributario (RUT)",
      value: fmtYesNo(rut),
      tone: rut === true ? "ok" : "neutral",
    },
    {
      label: "Operaciones internacionales",
      value: fmtYesNo(intlFlag),
      tone: "neutral",
    },
    {
      label: "Reclamos vigentes",
      value: fmtNumber(disputes),
      tone: isNum(disputes) && disputes > 0 ? "warn" : "neutral",
    },
  ];
};

export const mapPerson = (r?: HdcReport | null): PersonVM => {
  const b = r?.basicInformation;
  const personId = b?.personId ?? r?.identifyingAttributes?.personId;
  const composedName = [b?.names, b?.firstLastName, b?.secondLastName]
    .map((x) => (x ?? "").trim())
    .filter(Boolean)
    .join(" ");
  const id = b?.identification;
  const issueParts = [id?.issueDate, id?.issuingCityName]
    .map((x) => fmtText(x))
    .filter((x) => x !== DASH);
  const age = b?.age;
  let ageText = DASH;
  if (age?.minDesc || age?.maxDesc) {
    ageText = [age.minDesc, age.maxDesc].map(fmtText).join(" – ");
  } else if (isNum(age?.min) || isNum(age?.max)) {
    ageText = `${fmtNumber(age?.min)} – ${fmtNumber(age?.max)} años`;
  }
  return {
    fullName: firstText(
      b?.fullName,
      composedName,
      r?.identifyingAttributes?.personLastName,
    ),
    idDesc: fmtText(personId?.personIdDesc),
    idNumber: fmtDocNumber(personId?.personIdNumber),
    status: fmtText(id?.statusDesc),
    issue: issueParts.length ? issueParts.join(" · ") : DASH,
    age: ageText,
    gender: fmtText(b?.genderDesc),
  };
};

/** Experian Acierta-family scores range up to 950. */
export const SCORE_MAX = 950;

export const mapScore = (r?: HdcReport | null): ScoreVM | null => {
  const m = arr(r?.models)[0];
  if (!m) return null;
  const v = m.scoreValue;
  return {
    value: fmtNumber(v),
    pct: isNum(v) ? Math.max(0, Math.min(100, (v / SCORE_MAX) * 100)) : null,
    max: fmtNumber(SCORE_MAX),
    model: firstText(m.modelCodeDesc, arr(m.modelName).join(" "), m.modelCode),
    date: fmtText(m.modelDate),
    reasons: arr(m.ScoreReason)
      .map((x) => x.trim())
      .filter(Boolean),
  };
};

/** Aggregated KPIs; monetary values are thousands of COP. */
export const mapKpis = (r?: HdcReport | null): KpiVM[] => {
  const b = r?.agregatedInfo?.overview?.balances;
  const p = r?.agregatedInfo?.overview?.principals;
  if (!b && !p) return [];
  const overdue = b?.totalValueBalanceOverdue;
  return [
    {
      label: "Saldo total",
      value: fmtMoney(b?.totaldebtBalance),
      sub: `Máx. histórico ${fmtMoney(b?.HightestDebtBalance)}`,
      tone: "neutral",
    },
    {
      label: "Cuota mensual",
      value: fmtMoney(b?.valueMonthlyPayment),
      sub: `${fmtNumber(p?.currentCredits)} obligaciones vigentes`,
      tone: "neutral",
    },
    {
      label: "Saldo en mora",
      value: fmtMoney(overdue),
      sub: `D30 ${fmtMoney(b?.debtBalanceD30)} · D60 ${fmtMoney(
        b?.debtBalanceD60,
      )} · D90 ${fmtMoney(b?.debtBalanceD90)}`,
      tone: isNum(overdue) && overdue > 0 ? "bad" : "neutral",
    },
    {
      label: "Créditos vigentes",
      value: fmtNumber(p?.currentCredits),
      sub: `${fmtNumber(p?.closedCredits)} cerrados · ${fmtNumber(
        p?.currentNegativeCredits,
      )} negativos actuales`,
      tone: "neutral",
    },
    {
      label: "Consultas últimos 6 meses",
      value: fmtNumber(p?.consultedLast6Months),
      sub: `Negativos últimos 12 m: ${fmtNumber(
        p?.negativeHistoricalLast12Months,
      )}`,
      tone: "neutral",
    },
    {
      label: "Reclamos vigentes",
      value: fmtNumber(p?.currentDisputes),
      sub: `${fmtNumber(p?.totalDisputes)} en total`,
      tone:
        isNum(p?.currentDisputes) && (p?.currentDisputes ?? 0) > 0
          ? "warn"
          : "neutral",
    },
  ];
};

// endregion

// region obligaciones / tarjetas

const installmentsText = (v: HdcAccountValue | null): string => {
  if (!v) return DASH;
  const paid = v.paidInstallments;
  const total = v.totalNumberOfInstallments;
  if (!isNum(paid) && !isNum(total)) return DASH;
  return `${fmtNumber(paid)}/${fmtNumber(total)}`;
};

export const mapLiabilities = (r?: HdcReport | null): LiabilityRowVM[] =>
  arr(r?.liabilities).map((l) => {
    const a = l.account;
    const v = latestValue(l.values);
    const overdue = v?.businessValueBalanceOverdue;
    const statusText = firstText(
      l.status?.account?.businessAccountStatusDesc,
      l.status?.payment?.businessBureauEventDesc,
    );
    return {
      entity: fmtText(a?.businessLineName),
      type: firstText(
        a?.subAccountTypeDesc,
        l.featuresLiabilities?.typeOfCreditDesc,
        a?.accountTypeDesc,
      ),
      account: fmtText(a?.accountNumber),
      debtor: fmtText(l.featuresLiabilities?.typeOfDebtorDesc),
      status: { text: statusText, tone: statusTone(statusText, overdue) },
      opened: fmtText(a?.accountOpeningDate),
      initial: fmtNumber(v?.initialValue),
      balance: fmtNumber(v?.debtBalance),
      overdue: fmtNumber(overdue),
      hasOverdue: isNum(overdue) && overdue > 0,
      quota: fmtNumber(v?.valueMonthlyPayment),
      installments: installmentsText(v),
      rating: firstText(v?.rating, a?.ratingDesc),
      vector: parseVector(l.liabilitiesAccount?.businessBehaviourVectorProduct),
    };
  });

export const mapCreditCards = (r?: HdcReport | null): CardRowVM[] =>
  arr(r?.creditCard).map((c) => {
    const a = c.account;
    const v = latestValue(c.values);
    const limit = v?.initialValue;
    const balance = v?.debtBalance;
    const overdue = v?.businessValueBalanceOverdue;
    const available = isNum(v?.availableBalance)
      ? v?.availableBalance
      : isNum(limit) && isNum(balance)
      ? limit - balance
      : null;
    const statusText = firstText(
      c.status?.card?.cardStatusName,
      c.status?.account?.businessAccountStatusDesc,
    );
    return {
      entity: fmtText(a?.businessLineName),
      franchise: fmtText(c.FeaturesCreditCard?.franchiseName),
      cardClass: fmtText(c.FeaturesCreditCard?.cardClassName),
      account: fmtText(a?.accountNumber),
      status: { text: statusText, tone: statusTone(statusText, overdue) },
      limit: fmtNumber(limit),
      balance: fmtNumber(balance),
      available: fmtNumber(available),
      usagePct:
        isNum(limit) && limit > 0 && isNum(balance)
          ? Math.max(0, (balance / limit) * 100)
          : null,
      quota: fmtNumber(v?.valueMonthlyPayment),
      overdue: fmtNumber(overdue),
      hasOverdue: isNum(overdue) && overdue > 0,
      vector: parseVector(c.creditCardAccount?.businessBehaviourVectorProduct),
    };
  });

// endregion

// region cuentas y consultas

export const mapSavings = (r?: HdcReport | null): SavingsRowVM[] =>
  arr(r?.savings).map((s) => ({
    entity: fmtText(s.account?.businessLineName),
    type: firstText(s.account?.accountTypeDesc, s.account?.subAccountTypeDesc),
    account: fmtText(s.account?.accountNumber),
    opened: fmtText(s.account?.accountOpeningDate),
    status: firstText(
      s.status?.businessBureauEventDesc,
      s.features?.accountCategoryDesc,
    ),
    city: fmtText(s.account?.filingCityName),
  }));

/** Most recent inquiry first. */
export const mapInquiries = (r?: HdcReport | null): InquiryRowVM[] =>
  arr(r?.inquiryFootprints)
    .map((q, i) => ({ q, i, t: dateValue(q.inquiryDate) }))
    .sort((a, b) =>
      a.t !== null && b.t !== null ? b.t - a.t : a.i - b.i,
    )
    .map(({ q }) => ({
      date: fmtText(q.inquiryDate),
      entity: fmtText(q.inquiryBusinessName),
      product: firstText(q.accountTypeDesc, q.accountTypeName),
      city: fmtText(q.filingCityName),
      reason: fmtText(q.inquiryReasonDesc),
    }));

// endregion

// region endeudamiento global

export const mapGlobalIndebtedness = (
  r?: HdcReport | null,
): GlobalIndebtednessVM => {
  const list = arr(r?.globalIndebtedness);
  const unique = (xs: string[]) =>
    Array.from(new Set(xs.filter((x) => x !== DASH)));
  const sources = unique(
    list.map((g) =>
      firstText(g.sourceGlobalIndebtednessDesc, g.sourceGlobalIndebtedness),
    ),
  );
  const cutoffs = unique(list.map((g) => fmtText(g.cutoffDate))).sort();
  return {
    source: sources.length ? sources.join(", ") : DASH,
    cutoff: cutoffs.length ? cutoffs[cutoffs.length - 1] : DASH,
    rows: list.map((g) => ({
      entity: fmtText(g.entity?.businessLineName),
      sector: fmtText(g.entity?.economicSectorName),
      rating: firstText(g.quarterQualification, g.quarterQualificationDesc),
      credit: firstText(g.typeOfCreditDesc, g.typeOfCredit),
      count: fmtNumber(g.quantityOfObligations),
      capital: fmtNumber(g.capitalValue),
      guarantee: fmtText(g.Guarantee?.guaranteeTypeName),
      guaranteeValue: fmtNumber(g.Guarantee?.guaranteeValue),
    })),
  };
};

// endregion

// region evolución

/** Quarters in the order Experian sends them; values in thousands of COP. */
export const mapDebtEvolution = (r?: HdcReport | null): TrimesterVM[] =>
  arr(r?.agregatedInfo?.debtEvolution?.trimester).map((t) => {
    const d = t.DebBasic;
    const delinquency = fmtText(d?.maximumDelinquency);
    const isClean =
      delinquency === DASH || /^M\s*0$/i.test(delinquency.trim());
    return {
      label: fmtMonthYear(t.trimesterDate),
      balanceRaw: isNum(d?.debtBalance) ? d!.debtBalance! : null,
      balance: fmtNumber(d?.debtBalance),
      quota: fmtNumber(t.quota),
      delinquency,
      delinquencyTone: isClean ? "neutral" : "warn",
      // Per the DTO docs, DebBasic.valueMonthlyPayment is the rating letter.
      rating: fmtText(d?.valueMonthlyPayment),
      use: fmtPercent(d?.usePercentage),
      opened: fmtNumber(d?.totalOpened ?? d?.openingAccounts),
      closed: fmtNumber(d?.totalClosed ?? d?.closingAccounts),
      score: fmtNumber(d?.scoreValue),
    };
  });

// endregion

// region microcrédito

const cellsFor = (
  months: string[],
  dates?: (HdcCharacterDate | null)[] | null,
): VectorCell[] => {
  const byDate = new Map<string, string | null | undefined>();
  for (const c of arr(dates)) {
    if (c.behaviourDate) byDate.set(c.behaviourDate, c.totalValueBalanceOverdue);
  }
  return months.map((m) => vectorCell(byDate.get(m)));
};

/**
 * Sector/account behaviour grid. Columns are the union of behaviourDates in the
 * order they first appear (Experian's order), labelled by month.
 */
export const mapVectorAnalysis = (
  r?: HdcReport | null,
): VectorAnalysisVM => {
  const sectors = arr(r?.agregatedInfoMicrocredit?.vectorAnalysis?.sector);
  const months: string[] = [];
  const seen = new Set<string>();
  const collect = (dates?: (HdcCharacterDate | null)[] | null) => {
    for (const c of arr(dates)) {
      if (c.behaviourDate && !seen.has(c.behaviourDate)) {
        seen.add(c.behaviourDate);
        months.push(c.behaviourDate);
      }
    }
  };
  for (const s of sectors) {
    collect(s.maximumOverdue?.characterDate);
    for (const a of arr(s.account)) collect(a.characterDate);
  }

  const rows: VectorRowVM[] = [];
  for (const s of sectors) {
    rows.push({
      label: `${fmtText(s.economicSectorName)} · mora máx.`,
      isSector: true,
      cells: cellsFor(months, s.maximumOverdue?.characterDate),
    });
    for (const a of arr(s.account)) {
      const parts = [fmtText(a.businessName), fmtText(a.accountType)].filter(
        (x) => x !== DASH,
      );
      rows.push({
        label: parts.length ? parts.join(" · ") : DASH,
        isSector: false,
        cells: cellsFor(months, a.characterDate),
      });
    }
  }
  return { months: months.map(fmtMonth), rows };
};

const sectorRow = (
  label: string,
  c?: HdcSectorCounts | null,
): ProfileRowVM => ({
  label,
  fin: fmtNumber(c?.financialSector),
  coop: fmtNumber(c?.cooperativeSector),
  retail: fmtNumber(c?.retailSector),
  telco: fmtNumber(c?.telcosSector),
  principal: fmtNumber(c?.totalAsPrincipal),
  other: fmtNumber(c?.totalAsCosignerAndOther),
});

const antiquityRow = (d?: HdcSectorDates | null): ProfileRowVM => ({
  label: "Antigüedad desde",
  fin: fmtText(d?.financialSector),
  coop: fmtText(d?.cooperativeSector),
  retail: fmtText(d?.retailSector),
  telco: fmtText(d?.telcosSector),
  principal: DASH,
  other: DASH,
});

export const mapGeneralProfile = (r?: HdcReport | null): ProfileRowVM[] => {
  const g = r?.agregatedInfoMicrocredit?.overview?.generalProfile;
  if (!g) return [];
  return [
    antiquityRow(g.Antiquity?.sectorXDebtor),
    sectorRow("Créditos vigentes", g.currentCredits),
    sectorRow("Créditos cerrados", g.closedDebts),
    sectorRow("Reestructurados", g.RestructuredDebts),
    sectorRow("Refinanciados", g.refinancedDebts),
    sectorRow("Consultas últimos 6 m", g.inquiredLast6Months),
    sectorRow("Reclamos", g.disputes),
  ];
};

// endregion
